import { useCallback, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";

import { BannerQueueProvider, BannerStack, useBannerQueue } from "./BannerQueue";

export type ImportSuccessBannerData = {
  fillsImported: number;
  tradeCount: number;
  newInstrumentCount: number;
};

type ImportSuccessBannerHookValue = {
  show: (data: ImportSuccessBannerData) => void;
  dismiss: () => void;
};

// M10-U3: the V2-3-U5 ImportSuccessBanner surface is preserved as a thin
// wrapper over the generic BannerQueue. ImportSuccessBannerProvider mounts
// the queue; useImportSuccessBanner translates the legacy API to a queue
// show() call with a fixed id; ImportSuccessBanner is the stack renderer.

const IMPORT_SUCCESS_BANNER_ID = "import-success-banner";

export function ImportSuccessBannerProvider({ children }: { children: ReactNode }) {
  return <BannerQueueProvider>{children}</BannerQueueProvider>;
}

export function ImportSuccessBanner() {
  return <BannerStack />;
}

function ImportSuccessBannerBody({ data }: { data: ImportSuccessBannerData }) {
  const { fillsImported, tradeCount, newInstrumentCount } = data;
  return (
    <>
      Imported <strong>{fillsImported}</strong> fill(s) into{" "}
      <strong>{tradeCount}</strong> trade(s).
      {newInstrumentCount > 0 && (
        <>
          {" "}
          <strong>{newInstrumentCount}</strong> new instrument(s) auto-created.
        </>
      )}{" "}
      <Link
        to="/trades"
        className="font-medium text-primary underline-offset-2 hover:underline"
        data-testid="import-success-banner-view-trades"
      >
        View trades
      </Link>
      {newInstrumentCount > 0 && (
        <>
          {" · "}
          <Link
            to="/instruments"
            className="font-medium text-primary underline-offset-2 hover:underline"
            data-testid="import-success-banner-view-instruments"
          >
            View instruments
          </Link>
        </>
      )}
    </>
  );
}

export function useImportSuccessBanner(): ImportSuccessBannerHookValue {
  const queue = useBannerQueue();
  const show = useCallback(
    (data: ImportSuccessBannerData) => {
      queue.show({
        id: IMPORT_SUCCESS_BANNER_ID,
        kind: "success",
        body: <ImportSuccessBannerBody data={data} />,
        testId: IMPORT_SUCCESS_BANNER_ID,
      });
    },
    [queue],
  );
  const dismiss = useCallback(() => {
    queue.dismiss(IMPORT_SUCCESS_BANNER_ID);
  }, [queue]);
  return { show, dismiss };
}
